import { User as FirebaseUser } from "@firebase/auth";
import { multiFactor, onAuthStateChanged } from "firebase/auth";
import { makeAutoObservable, runInAction } from "mobx";
import posthog from "posthog-js";
import { BriaAxios } from "../../config/axios";
import { ENV_VARS, isIntegration } from "../../config/env.ts";
import { firebaseAuth } from "../../config/firebase.ts";
import { POSTHOG_EVENTS } from "../../constants/PostHogEvents";
import { ORG_BLOCK_REASONS, ORG_SUBSCRIPTION_PLANS, ORG_SUBSCRIPTION_STATUSES } from "../../constants/billing.ts";
import { AESCipher } from "../../helpers/encryption.ts";
import { getUserWithUID } from "../../helpers/firebase.ts";
import {
	clearSelectedOrganization,
	getSelectedOrganization,
	setSelectedOrganization,
} from "../../helpers/localStorage.ts";
import { IRootStore } from "../../mobx/root-store.tsx";
import { OrgFeatures, OrgSubscription, OrgUsage } from "../../models/billing.ts";
import { RelatedOrganization, UserOrganization } from "../../models/organization.ts";
import User from "../../models/user.ts";
import BillingService from "../../services/BillingService.ts";
import postHogService from "../../services/PostHogService";
import { showErrorToast } from "../../utils/toast.tsx";
import iframeStore from "../IframeNew/iframe-store.tsx";

function userFromSessionData(data: Record<string, unknown>): User {
	return new User(
		(data.user_name as string) ?? "",
		(data.uid as string) ?? "",
		(data.profile_picture as string) ?? "",
		(data.role as string) ?? "",
		(data.company as string) ?? "",
		(data.userRole as string) ?? "",
		(data.getInfo as boolean) ?? false,
		(data.email as string) ?? "",
		data.settings,
		data.user_organizations,
		(data.leadSource as string) ?? undefined,
		(data.otherLeadSourceDetails as string) ?? undefined,
		(data.otherRoleDetails as string) ?? undefined,
	);
}

export interface IAuthStore {
	user: User | null;
	isLoadingUserOrganizations: boolean;
	userOrganizations: UserOrganization[];
	isLoggedIn: boolean;
	isLoading: boolean;
	isInitialized: boolean;
	isLoadingOrgSubscriptions: boolean;
	isLoadingOrgUsage: boolean;
	isOrgBlocked: boolean;
	orgPassedFreeLimit: boolean;
	isSubscriptionInactive: boolean;
	isPremiumOrgSubscription: boolean;
	isError: boolean;
	orgSubscription: OrgSubscription | null;
	orgUsage: OrgUsage | null;
	registrationSource?: string | null;
	parentOrganization: RelatedOrganization | null;
	subOrganizations: RelatedOrganization[];
	isParentOrganizationLoading: boolean;
	isSubOrganizationsLoading: boolean;
	isCreateSubOrgsLoading: boolean;
	isMfaSetupRequired?: boolean;
	isMfaSetupCompleted?: boolean;
	isFirstLogin: boolean;
	billingService: BillingService;
	isLoginProcess: boolean;
	openInvoiceDetails: any;
	isLoadingOpenInvoiceDetails: boolean;
	isRedirectingToLogout: boolean;

	resetStore(): void;

	logout(): Promise<void>;

	setUser(user: User | null): Promise<void>;

	grantUserPermission(orgId: string): Promise<void>;

	createOrganization(org_name: string): Promise<any>;

	updateOrganizationName(name: string): Promise<{ id: string; name: string; updated_at: string }>;

	updateSelectedOrgNameInLocalState(name: string): void;

	createSubOrganization(orgName: string, matchParentPlan: boolean): Promise<any>;

	searchOrganizations(query: string, signal?: AbortSignal): Promise<UserOrganization[]>;

	loadUserData(): Promise<void>;

	loadOrgSubscription(): Promise<void>;

	isFeatureEnabled(feature: OrgFeatures): boolean;

	loadOrgUsage(): Promise<void>;

	getLatestOpenInvoiceDetails(): Promise<void>;

	loadOrganization(orgId: string): Promise<UserOrganization>;

	setPostRegistrationConfigs(): Promise<void>;

	loadOrganizations(): Promise<any>;

	loadParentOrganization(): Promise<any>;

	loadSubOrganizations(): Promise<any>;

	blockOrganization(orgID: string): Promise<any>;

	unBlockOrganization(orgID: string): Promise<any>;

	isOrgFromAWS(): Promise<boolean>;

	awsAuthenticate(awsRegistrationToken: string): Promise<any>;

	awsAuthenticate(awsRegistrationToken: string): Promise<any>;

	checkMfaEnabled: () => Promise<boolean>;

	checkCurrentOrgMfa: () => Promise<boolean>;

	checkMfaSetupRequired: () => Promise<void>;

	userHasTotpEnrolled: () => Promise<boolean>;

	toggleOrgMfa: (currentOrgMfaStatus?: boolean) => Promise<void>;

	createMfaAttempt: (verificationStatus: boolean, encryptedEmail: string) => Promise<void>;

	checkMfaLock: (encryptedEmail: string) => Promise<any>;

	queryParams: URLSearchParams;
}

export default class AuthStore implements IAuthStore {
	rootStore: IRootStore;
	user: User | null = null;
	userOrganizations: UserOrganization[] = [];
	isLoggedIn = false;
	isLoginProcess = false;
	openInvoiceDetails = {};
	isLoadingOpenInvoiceDetails = false;

	isError = false;
	isLoading = true;
	isInitialized = false;
	isLoadingOrgSubscriptions = false;
	isLoadingUserOrganizations = false;
	isOrgBlocked = false;
	orgPassedFreeLimit = false;
	isSubscriptionInactive = false;
	isPremiumOrgSubscription = false;
	isLoadingOrgUsage = false;
	orgSubscription: OrgSubscription | null = null;
	orgUsage: OrgUsage | null = null;
	billingService = new BillingService();
	registrationSource = undefined;
	isFirstLogin = false;
	parentOrganization = null;
	subOrganizations: RelatedOrganization[] = [];
	isParentOrganizationLoading: boolean = false;
	isSubOrganizationsLoading: boolean = false;
	isCreateSubOrgsLoading: boolean = false;
	isMfaSetupRequired: boolean = false;
	isMfaSetupCompleted: boolean = false;
	queryParams: URLSearchParams = new URLSearchParams(location.search);
	private sessionStartTime: number | null = null;
	/** Set true at start of logout so PrivateLayout does not redirect to new platform /login. */
	isRedirectingToLogout = false;

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;

		if (iframeStore.isIframe()) {
			onAuthStateChanged(firebaseAuth, async (user) => {
				if (user) {
					this.isLoading = true;
					this.isError = false;

					getUserWithUID(user.uid)
						.then(async (userObject) => {
							if (userObject) {
								await this.setUser(userObject);
							} else {
								await this.setUser(null);
							}
						})
						.catch(async (error) => {
							console.log(error);
							await this.setUser(null);
						})
						.finally(() => {
							runInAction(() => {
								this.isInitialized = true;
							});
						});
				} else {
					await this.setUser(null);
					runInAction(() => {
						this.isInitialized = true;
					});
				}
			});
		} else {
			void this.initializeSessionUser();
		}
	}

	/** Fetches user from /session/user_data (session cookie). Sets user or null and marks initialized. */
	private async initializeSessionUser(): Promise<void> {
		try {
			this.isLoading = true;
			this.isError = false;
			const res = await (await BriaAxios()).get("/session/user_data");
			const data = res.data;
			if (data && typeof data === "object" && data.uid) {
				const user = userFromSessionData(data as Record<string, unknown>);
				await this.setUser(user);
			} else {
				await this.setUser(null);
			}
		} catch {
			await this.setUser(null);
		} finally {
			runInAction(() => {
				this.isInitialized = true;
			});
		}
	}

	resetStore = (): void => {
		this.orgSubscription = null;
		this.isFirstLogin = false;
		this.orgUsage = null;
		this.parentOrganization = null;
		this.subOrganizations = [];
	};

	logout = async (): Promise<void> => {
		this.isRedirectingToLogout = true;
		try {
			// Capture user data BEFORE clearing anything
			const sessionDurationMs = this.sessionStartTime ? Date.now() - this.sessionStartTime : 0;
			const userName = this.user?.userName;
			const userEmail = this.user?.email;

			// Format session duration to human readable format
			const formatSessionDuration = (ms: number): string => {
				const seconds = Math.floor(ms / 1000);
				const minutes = Math.floor(seconds / 60);
				const hours = Math.floor(minutes / 60);

				if (hours > 0) {
					return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
				} else if (minutes > 0) {
					return `${minutes}m ${seconds % 60}s`;
				} else {
					return `${seconds}s`;
				}
			};

			// Track logout
			postHogService.trackEvent(POSTHOG_EVENTS.LOGOUT, {
				session_duration: formatSessionDuration(sessionDurationMs),
				user_name: userName,
				email: userEmail,
			});

			// Redirect to new platform /logout to clear session cookie and local state there
			this.isLoginProcess = false;
			this.rootStore.resetStores();
			await this.rootStore.analyticsStore.setUser(null);

			// Reset PostHog after tracking logout
			posthog.reset();
			this.sessionStartTime = null;

			// Clear usage engagement popup state on logout
			this.rootStore.fiboUsageStore.resetUsageEngagementPopupState();

			this.isLoggedIn = false;
			this.isMfaSetupRequired = false;
			this.isMfaSetupCompleted = false;
			localStorage.clear();
			window.location.href = `${ENV_VARS.VITE_NEW_PLATFORM_URL}/logout`;
		} catch (e) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	};

	setUser = async (user: User | null): Promise<void> => {
		try {
			this.user = user;
			if (user != null) {
				this.rootStore.analyticsStore.setUser(user, this.registrationSource);

				// Identify user in PostHog and start session tracking
				this.sessionStartTime = Date.now();

				// Reset usage engagement popup state
				this.rootStore.fiboUsageStore.resetUsageEngagementPopupState();

				posthog.identify(user.email, {
					...user,
				});

				if (window.opener && !window.opener.closed) {
					let targetOrigin = ENV_VARS.VITE_NEW_PLATFORM_URL;
					const message = JSON.stringify({ type: "LOGIN_SUCCESS" });
					console.log(message, targetOrigin);
					window.opener.postMessage(message, targetOrigin);
					if (isIntegration()) {
						// localhost support on int
						targetOrigin = "https://local.bria.ai:5174";
						console.log(message, targetOrigin);
						window.opener.postMessage(message, targetOrigin);
					}
				}

				await this.loadUserData();
			} else {
				// Reset PostHog when user logs out
				posthog.reset();
			}
			this.isLoggedIn = user !== null;
			this.isLoading = false;
			this.isError = false;
		} catch (e) {
			runInAction(() => {
				this.isError = true;
				this.isLoading = false;
			});
			showErrorToast();
			return Promise.reject(e);
		} finally {
			this.isInitialized = true;
		}
	};

	createOrganization = async (org_name: string): Promise<any> => {
		this.isLoading = false;
		this.isError = false;
		const data = {
			name: org_name,
			display_name: org_name,
		};
		try {
			const response = await (await BriaAxios()).post(`/create_organization/`, data);

			// Track organization creation
			const isFirstOrg = this.userOrganizations.length === 0;
			postHogService.trackEvent(POSTHOG_EVENTS.ORGANIZATION_CREATED, {
				org_name: org_name,
				is_first_org: isFirstOrg,
			});

			runInAction(() => {
				this.isLoading = false;
				this.isError = false;
			});
			if (this.user) {
				getUserWithUID(this.user.uid).then((userObject) => {
					if (userObject) {
						this.setUser(userObject);
					} else {
						this.setUser(null);
					}
				});
			}
			return response.data;
		} catch (e) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	};

	loadOrganization = async (orgId: string): Promise<UserOrganization> => {
		try {
			const response = await (await BriaAxios()).get(`/organization/${orgId}`);
			return response.data as UserOrganization;
		} catch (e) {
			return Promise.reject(e);
		}
	};

	updateOrganizationName = async (name: string) => {
		const selected = getSelectedOrganization();
		if (!selected) return Promise.reject(new Error("No organization selected"));
		const response = await (await BriaAxios()).patch(`/org/${selected.organization.uid}`, { name });
		return response.data as { id: string; name: string; updated_at: string };
	};

	updateSelectedOrgNameInLocalState = (name: string): void => {
		const selectedOrg = getSelectedOrganization();
		if (!selectedOrg) return;
		const updatedOrg: UserOrganization = {
			...selectedOrg,
			organization: { ...selectedOrg.organization, display_name: name },
		};
		runInAction(() => {
			const idx = this.userOrganizations.findIndex((o) => o.organization.uid === selectedOrg.organization.uid);
			if (idx >= 0) {
				this.userOrganizations = [
					...this.userOrganizations.slice(0, idx),
					updatedOrg,
					...this.userOrganizations.slice(idx + 1),
				];
			}
		});
		setSelectedOrganization(updatedOrg);
	};

	loadOrganizations = async (): Promise<any> => {
		this.isLoadingUserOrganizations = true;
		try {
			let response: any;
			if (this.user?.isSuperAdmin()) {
				response = await this.searchOrganizations("bria");
			} else {
				response = await (await BriaAxios()).get("/organizations/");
				response = response.data;
			}
			runInAction(() => {
				this.userOrganizations = response;

				const selectedOrg = getSelectedOrganization();
				if (this.userOrganizations.length > 0) {
					if (!selectedOrg) {
						if (this.userOrganizations.length === 1 || this.user?.isSuperAdmin()) {
							setSelectedOrganization(this.userOrganizations[0]);
						}
					} else {
						const foundOrg = this.userOrganizations.find(
							(org) => org.organization.uid === selectedOrg.organization.uid,
						);
						if (foundOrg) {
							setSelectedOrganization(foundOrg);
						} else {
							if (this.user?.isSuperAdmin()) {
								this.userOrganizations = [selectedOrg, ...this.userOrganizations];
							} else {
								setSelectedOrganization(this.userOrganizations[0]);
							}
						}
					}
				} else {
					clearSelectedOrganization();
				}
			});
		} catch (e) {
			this.isError = true;
			this.isLoading = false;
			return Promise.reject(e);
		} finally {
			this.isLoadingUserOrganizations = false;
		}
		return this.userOrganizations;
	};

	loadUserData = async () => {
		if (!iframeStore.isIframe()) {
			await this.loadOrganizations();
		}
		if (
			getSelectedOrganization() &&
			(this.userOrganizations.length > 0 || (iframeStore.isIframe() && !iframeStore.isPreview()))
		) {
			await this.loadOrgSubscription();
			if (!iframeStore.isIframe()) {
				this.loadOrgUsage();
				this.getLatestOpenInvoiceDetails();
				await this.checkMfaSetupRequired();
			}
		}
	};

	searchOrganizations = async (query: string, signal?: AbortSignal) => {
		const res = await (
			await BriaAxios()
		).get("/search_organizations/", {
			params: {
				query,
			},
			signal,
		});
		return res.data;
	};

	setPostRegistrationConfigs = async (): Promise<void> => {
		try {
			await (await BriaAxios()).post("/users/set_post_registration_configs", { urlQuery: location.search });
		} catch (e) {
			return Promise.reject(e);
		}
	};

	isFeatureEnabled = (feature: OrgFeatures) => {
		// you should use this only if the isLoadingOrgSubscriptions is false, otherwise, add a loader in the frontend based on isLoadingOrgSubscriptions
		return this.orgSubscription?.features?.includes(feature) ?? false;
	};

	loadOrgSubscription = async (): Promise<void> => {
		runInAction(() => {
			this.isError = false;
			this.isLoadingOrgSubscriptions = true;
		});
		try {
			const response = await this.billingService.loadOrgSubscription();
			runInAction(() => {
				this.orgSubscription = response;
				this.isError = false;

				const plan = this.orgSubscription?.plan_name ?? ORG_SUBSCRIPTION_PLANS.free.name;
				this.isPremiumOrgSubscription =
					plan !== ORG_SUBSCRIPTION_PLANS.free.name && plan !== ORG_SUBSCRIPTION_PLANS.starter.name;
				this.isOrgBlocked =
					(this.orgSubscription?.is_blocked ?? false) &&
					this.orgSubscription?.block_reason !== ORG_BLOCK_REASONS.PASSED_FREE_LIMITS;
				this.isLoadingOrgSubscriptions = false;
				const newOrgPassedFreeLimit =
					(this.orgSubscription?.is_blocked ?? false) &&
					this.orgSubscription?.plan_name === ORG_SUBSCRIPTION_PLANS.free.name &&
					this.orgSubscription?.block_reason === ORG_BLOCK_REASONS.PASSED_FREE_LIMITS;

				this.orgPassedFreeLimit = newOrgPassedFreeLimit;
				this.isSubscriptionInactive =
					!this.orgPassedFreeLimit &&
					((this.orgSubscription?.is_blocked ?? false) ||
						(this.orgSubscription?.status !== ORG_SUBSCRIPTION_STATUSES.PAST_DUE &&
							this.orgSubscription?.status !== ORG_SUBSCRIPTION_STATUSES.ACTIVE));
			});
			return response;
		} catch (e) {
			runInAction(() => {
				this.isLoadingOrgSubscriptions = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	};

	loadOrgUsage = async (): Promise<void> => {
		runInAction(() => {
			this.isError = false;
			this.isLoadingOrgUsage = true;
		});
		try {
			const response = await this.billingService.loadOrgUsage();
			runInAction(() => {
				this.orgUsage = response;
				this.isError = false;
				this.isLoadingOrgUsage = false;
			});
		} catch (e) {
			runInAction(() => {
				this.isLoadingOrgUsage = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	};

	getLatestOpenInvoiceDetails = async (): Promise<void> => {
		runInAction(() => {
			this.isError = false;
			this.isLoadingOpenInvoiceDetails = true;
		});
		try {
			const response = await this.billingService.getLatestOpenInvoiceDetails();
			runInAction(() => {
				this.openInvoiceDetails = response;
				this.isError = false;
				this.isLoadingOpenInvoiceDetails = false;
			});
		} catch (e) {
			runInAction(() => {
				this.isLoadingOpenInvoiceDetails = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	};

	grantUserPermission = async (orgId: string): Promise<void> => {
		const aesCipher = new AESCipher();
		const encryptedData = await aesCipher.encrypt(orgId);
		await (await BriaAxios()).post(`/grant_permission/`, { enc: encryptedData });
	};

	loadParentOrganization = async (): Promise<any> => {
		this.isParentOrganizationLoading = true;
		this.isError = false;
		try {
			const response = await (await BriaAxios()).get(`/organizations/parent_organization`);
			runInAction(() => {
				this.parentOrganization = response.data.parent_org;
			});
		} catch (e) {
			runInAction(() => {
				this.isError = true;
			});
			return Promise.reject(e);
		} finally {
			this.isParentOrganizationLoading = false;
		}
	};

	loadSubOrganizations = async (): Promise<any> => {
		this.isSubOrganizationsLoading = true;
		this.isError = false;
		try {
			const response = await (await BriaAxios()).get(`/organizations/sub_organizations`);
			runInAction(() => {
				this.subOrganizations = response.data;
			});
		} catch (e) {
			runInAction(() => {
				this.isError = true;
			});
			return Promise.reject(e);
		} finally {
			this.isSubOrganizationsLoading = false;
		}
	};

	createSubOrganization = async (orgName: string, matchParentPlan: boolean): Promise<any> => {
		this.isCreateSubOrgsLoading = true;
		this.isError = false;
		const data = {
			name: orgName,
			display_name: orgName,
			match_parent_plan: matchParentPlan,
		};
		try {
			const selectedOrg = getSelectedOrganization();
			if (selectedOrg) {
				const response = await (await BriaAxios()).post(`/organizations/create_sub_organization`, data);

				// Track sub organization creation
				postHogService.trackEvent(POSTHOG_EVENTS.SUB_ORGANIZATION_CREATED, {
					parent_org: selectedOrg.organization.name,
					parent_org_id: selectedOrg.organization.uid,
					sub_org_name: orgName,
				});

				runInAction(() => {
					this.isCreateSubOrgsLoading = false;
					this.isError = false;
				});
				return response.data as UserOrganization;
			}
		} catch (e) {
			runInAction(() => {
				this.isCreateSubOrgsLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	};
	blockOrganization = async (orgID: string): Promise<any> => {
		this.isLoading = false;
		this.isError = false;
		try {
			await (await BriaAxios()).get(`/organizations/${orgID}/block`);
			runInAction(() => {
				this.isLoading = false;
				this.isError = false;
			});
		} catch (e) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	};

	unBlockOrganization = async (orgID: string): Promise<any> => {
		this.isLoading = false;
		this.isError = false;
		try {
			await (await BriaAxios()).get(`/organizations/${orgID}/unblock`);
			runInAction(() => {
				this.isLoading = false;
				this.isError = false;
			});
		} catch (e) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	};

	isOrgFromAWS = async (): Promise<boolean> => {
		try {
			runInAction(() => {
				this.isLoading = true;
			});
			const response = await (await BriaAxios()).get(`/marketplace/aws/is_org_aws`);
			runInAction(() => {
				this.isLoading = false;
			});
			return response.data.result as boolean;
		} catch (e) {
			return false;
		}
	};

	awsAuthenticate = async (awsRegistrationToken: string): Promise<any> => {
		this.isLoading = false;
		this.isError = false;

		const data = {
			awsRegistrationToken: awsRegistrationToken,
		};

		try {
			await (await BriaAxios()).post(`/marketplace/aws/aws-marketplace-authentication`, data);
			runInAction(() => {
				this.isLoading = false;
				this.isError = false;
			});
		} catch (e) {
			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
			});
			return Promise.reject(e);
		}
	};

	checkMfaEnabled = async (): Promise<boolean> => {
		const res = await (await BriaAxios()).get("mfa/check_enabled");
		return res.data.org_mfa_enabled;
	};

	userHasTotpEnrolled = async (): Promise<boolean> => {
		const currentUser: FirebaseUser | null = firebaseAuth.currentUser;
		if (!currentUser) return false;
		try {
			await currentUser.reload();
			const enrolledFactors = multiFactor(currentUser).enrolledFactors;
			return enrolledFactors.some((factor: any) => factor.factorId === "totp");
		} catch (e) {
			return false;
		}
	};

	checkMfaSetupRequired = async (): Promise<void> => {
		this.isMfaSetupCompleted = await this.userHasTotpEnrolled();
		this.isMfaSetupRequired = await this.checkMfaEnabled();
	};

	checkCurrentOrgMfa = async (): Promise<boolean> => {
		const res = await (await BriaAxios()).get("mfa/check_current_org");
		return res.data.org_mfa_enabled;
	};

	toggleOrgMfa = async (currentOrgMfaStatus?: boolean): Promise<void> => {
		if (!currentOrgMfaStatus) {
			currentOrgMfaStatus = await this.checkCurrentOrgMfa();
		}
		await (await BriaAxios()).post("mfa/toggle", { mfa_status: currentOrgMfaStatus });
	};

	createMfaAttempt = async (verificationStatus: boolean, encryptedEmail: string): Promise<void> => {
		await (
			await BriaAxios()
		).post("mfa/add_verification_attempt", { verification_status: verificationStatus, email: encryptedEmail });
	};

	checkMfaLock = async (encryptedEmail: string): Promise<any> => {
		const response = await (await BriaAxios()).post("mfa/check_lock", { email: encryptedEmail });
		return response.data;
	};
}
